import React, { useState, useEffect } from 'react';
import IndexCard from './IndexCard';

export default function Drawer({ user, books, caller, onClaim, onUnclaim, onDelete, onSelfStatus, onCopy, onEditSeries, onConvertToSeries, onEditSave, onAdd, onReorder, defaultOpen }) {
    const [open, setOpen] = useState(defaultOpen);
    const isYou = user.name === caller;
    const mine = books.filter(b => b.addedBy === user.name);

    const [dragId, setDragId] = useState(null);
    const [overId, setOverId] = useState(null);

    // Auto-scroll the page when dragging near the top or bottom edge of the viewport.
    // The HTML5 drag API doesn't do this on mobile, so we drive it ourselves.
    useEffect(() => {
        if (!dragId) return;

        const ZONE = 100;   // px from edge that triggers scroll
        const MAX_SPEED = 14; // px per frame at the very edge
        let pointerY = null;
        let rafId;

        const onDragOver = e => { pointerY = e.clientY; };

        const tick = () => {
            if (pointerY !== null) {
                const h = window.innerHeight;
                if (pointerY < ZONE) {
                    window.scrollBy(0, -Math.ceil(MAX_SPEED * (1 - pointerY / ZONE)));
                } else if (pointerY > h - ZONE) {
                    window.scrollBy(0, Math.ceil(MAX_SPEED * (1 - (h - pointerY) / ZONE)));
                }
            }
            rafId = requestAnimationFrame(tick);
        };

        window.addEventListener('dragover', onDragOver);
        rafId = requestAnimationFrame(tick);

        return () => {
            window.removeEventListener('dragover', onDragOver);
            cancelAnimationFrame(rafId);
        };
    }, [dragId]);

    function handleDragStart(id, e) {
        setDragId(id);
        try { e.dataTransfer.setData('text/plain', id); } catch {}
        e.dataTransfer.effectAllowed = 'move';
    }
    function handleDragOver(id, e) {
        if (!dragId) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (overId !== id) setOverId(id);
    }
    function handleDragLeave(id) {
        if (overId === id) setOverId(null);
    }
    function handleDrop(id, e) {
        e.preventDefault();
        if (dragId && dragId !== id) onReorder(user.name, dragId, id);
        setDragId(null);
        setOverId(null);
    }
    function handleDragEnd() {
        setDragId(null);
        setOverId(null);
    }

    return (
        <section className={'drawer' + (open ? ' is-open' : '') + (isYou ? ' is-you' : '')}>
            <header className="drawer-hd" onClick={() => setOpen(v => !v)}>
                <span className="drawer-tab" data-c={user.color}>{user.initial}</span>
                <div className="drawer-meta">
                    <h2 className="drawer-name">
                        {user.name}
                        {isYou && <span className="drawer-you-tag">· you</span>}
                    </h2>
                    <span className="drawer-sub">
                        {user.classification} · {mine.length} entr{mine.length === 1 ? 'y' : 'ies'}
                    </span>
                </div>
                <div className="drawer-actions">
                    <span className="drawer-count">
                        {mine.length === 0 ? 'empty' : `${mine.length} to read`}
                    </span>
                    <span className="drawer-chev">›</span>
                </div>
            </header>

            {open && (
                <div className="drawer-body">
                    {isYou && (
                        <div className="spoiler-shield">
                            <span className="spoiler-shield-icon">No spoilers</span>
                            <span>
                                Purchase state on your own list is hidden — so surprises stay surprises.
                                You can still add, edit, or remove your entries.
                            </span>
                        </div>
                    )}

                    {isYou && mine.length > 0 && (
                        <div className="drawer-add-row">
                            <button className="add-btn" onClick={onAdd}>
                                <span className="add-btn-plus">+</span>
                                <span>Add a book or series</span>
                            </button>
                        </div>
                    )}

                    {mine.length === 0 ? (
                        <div className="drawer-empty">
                            {isYou
                                ? 'Nothing on your list yet. Add the first one below.'
                                : `${user.name} hasn't added anything yet.`}
                        </div>
                    ) : (
                        <ul className="cards-list">
                            {mine.map(b => (
                                <IndexCard
                                    key={b.id}
                                    book={b}
                                    caller={caller}
                                    isYourDrawer={isYou}
                                    onClaim={onClaim}
                                    onUnclaim={onUnclaim}
                                    onDelete={onDelete}
                                    draggable={isYou && mine.length > 1}
                                    isDragging={dragId === b.id}
                                    isDragOver={overId === b.id && dragId !== b.id}
                                    onDragStart={e => handleDragStart(b.id, e)}
                                    onDragOver={e => handleDragOver(b.id, e)}
                                    onDragLeave={() => handleDragLeave(b.id)}
                                    onDrop={e => handleDrop(b.id, e)}
                                    onDragEnd={handleDragEnd}
                                    onSelfStatus={onSelfStatus}
                                    onCopy={onCopy}
                                    onEditSeries={onEditSeries}
                                    onConvertToSeries={onConvertToSeries}
                                    onEditSave={onEditSave}
                                />
                            ))}
                        </ul>
                    )}

                    {isYou && mine.length > 1 && (
                        <div className="reorder-hint">
                            <span>⠇</span>
                            <span>drag the handle on the left to reorder your list</span>
                        </div>
                    )}

                    {isYou && (
                        <div className="drawer-add-row">
                            <button className="add-btn" onClick={onAdd}>
                                <span className="add-btn-plus">+</span>
                                <span>Add a book or series</span>
                            </button>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
